import React from 'react';
import { ArrowUpRight, Check, Eye, Flag, Phone, Sparkles } from 'lucide-react';
import { cn } from './utils/cn';
import { useAudio } from './AudioProvider';

function soundForAction(action) {
  const a = String(action || '').toUpperCase();
  if (a === 'FOLD') return 'fold';
  if (a === 'CALL') return 'call';
  if (a === 'CHECK') return 'check';
  if (a === 'RAISE' || a === 'ALL_IN' || a === 'ALLIN') return 'raise';
  if (a === 'LOOK') return 'card-flip';
  if (a === 'SHOWDOWN') return 'showdown';
  return 'button-click';
}

function iconForAction(action) {
  const a = String(action).toUpperCase();
  if (a === 'FOLD') return Flag;
  if (a === 'CALL') return Phone;
  if (a === 'CHECK') return Check;
  if (a === 'RAISE' || a === 'ALL_IN' || a === 'ALLIN') return ArrowUpRight;
  if (a === 'LOOK' || a === 'SHOWDOWN') return Eye;
  return Sparkles;
}

export function SekaActionBar({ actions, onAction, disabled }) {
  const { play } = useAudio();
  const gridClass =
    actions.length <= 4
      ? 'grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2'
      : 'grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2';

  return (
    <div className={cn('mx-auto w-full max-w-lg px-1 sm:px-0', gridClass)}>
      {actions.map(({ action, label }) => {
        const key = action;
        const base = cn(
          'relative flex min-h-[2.75rem] items-center justify-center rounded-xl border px-2 py-2.5 text-center',
          'text-[10px] font-semibold uppercase leading-snug tracking-tight transition-all duration-150 sm:min-h-[3rem] sm:px-2.5 sm:text-[11px]',
          'shadow-sm backdrop-blur-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'active:scale-[0.98] active:brightness-110 disabled:pointer-events-none disabled:opacity-45 disabled:saturate-50'
        );

        const styled =
          action === 'FOLD'
            ? cn(
                'border-red-500/30 bg-gradient-to-b from-red-950/55 to-red-950/35 text-red-100/95',
                'hover:border-red-400/45 hover:from-red-900/60 hover:to-red-950/45 hover:shadow-[0_6px_22px_rgba(239,68,68,0.18)]',
                'focus-visible:ring-red-400/40'
              )
            : action === 'CALL' || action === 'CHECK'
              ? cn(
                  'border-cyan-500/30 bg-gradient-to-b from-cyan-950/50 to-slate-950/40 text-cyan-100/95',
                  'hover:border-cyan-400/45 hover:from-cyan-900/55 hover:to-slate-900/45 hover:shadow-[0_6px_22px_rgba(34,211,238,0.18)]',
                  'focus-visible:ring-cyan-400/40'
                )
              : action === 'RAISE'
                ? cn(
                    'border-orange-400/45 bg-gradient-to-b from-orange-900/65 via-orange-950/50 to-slate-950/35 text-orange-50',
                    'hover:border-orange-300/60 hover:from-orange-800/70 hover:to-slate-900/40 hover:shadow-[0_8px_28px_rgba(251,146,60,0.25)]',
                    'ring-1 ring-orange-300/20',
                    'focus-visible:ring-orange-400/40'
                  )
                : action === 'SHOWDOWN' || action === 'LOOK'
                  ? cn(
                      'border-amber-400/35 bg-gradient-to-b from-amber-950/50 to-slate-950/35 text-amber-100/95',
                      'hover:border-amber-300/55 hover:from-amber-900/55 hover:to-slate-900/40 hover:shadow-[0_6px_22px_rgba(251,191,36,0.2)]',
                      'focus-visible:ring-amber-400/40'
                    )
                  : cn(
                      'border-border/60 bg-card/70 text-foreground hover:bg-card/90 hover:border-border',
                      'focus-visible:ring-muted-foreground/30'
                    );

        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            className={cn(base, styled)}
            onClick={() => {
              play(soundForAction(action));
              onAction(action);
            }}
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-xl bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-60" aria-hidden />
            <span className="flex items-center justify-center gap-1.5">
              {React.createElement(iconForAction(action), { className: 'h-3.5 w-3.5 shrink-0', 'aria-hidden': true })}
              <span>{label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
